import _extends from "@babel/runtime/helpers/esm/extends";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";

var _class, _class2, _temp;

import React from 'react';
import PropTypes from 'prop-types';
import { globalObject } from '@skbkontur/global-object';
import debounce from 'lodash.debounce';
import { isInstanceOf } from "../../../lib/isInstanceOf";
import * as LayoutEvents from "../../../lib/LayoutEvents";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { cx } from "../../../lib/theming/Emotion";
import { isIE11 } from "../../../lib/client";
import { rootNode } from "../../../lib/rootNode";
import { getDOMRect } from "../../../lib/dom/getDOMRect";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isTestEnv } from "../../../lib/currentEnvironment";
import { styles, globalClasses } from "../ScrollContainer.styles";
import { scrollSizeParametersNames } from "../ScrollContainer.constants";
import { getScrollYOffset, convertScrollbarXScrollState, convertScrollbarYScrollState } from "../ScrollContainer.helpers";
import { ScrollBar } from "../ScrollBar";
export var ScrollContainerDataTids = {
  root: 'ScrollContainer__root',
  inner: 'ScrollContainer__inner'
};
export var ScrollContainer = rootNode(_class = (_temp = _class2 = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(ScrollContainer, _React$Component);

  function ScrollContainer() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(ScrollContainer.defaultProps);
    _this.initialIsScrollBarVisible = !_this.getProps().hideScrollBar && _this.getProps().showScrollBar === 'always';
    _this.state = {
      isScrollBarXVisible: _this.initialIsScrollBarVisible,
      isScrollBarYVisible: _this.initialIsScrollBarVisible,
      isHovered: false
    };

    _this.render = function () {
      var props = _this.props;

      if (_this.props.disabled) {
        return _this.props.children;
      }

      var innerStyle = {
        scrollBehavior: _this.getProps().scrollBehaviour,
        maxHeight: props.maxHeight,
        maxWidth: props.maxWidth
      };

      var scrollbarY = _this.renderScrollbar('y');

      var scrollbarX = _this.renderScrollbar('x');

      return /*#__PURE__*/React.createElement(CommonWrapper, _extends({
        rootNodeRef: _this.setRootNode
      }, _this.props), /*#__PURE__*/React.createElement("div", {
        "data-tid": ScrollContainerDataTids.root,
        className: styles.root(),
        onMouseMove: _this.handleMouseMove,
        onMouseLeave: _this.handleMouseLeave
      }, scrollbarY, scrollbarX, /*#__PURE__*/React.createElement("div", {
        style: innerStyle,
        ref: _this.refInner,
        className: cx(styles.inner(), globalClasses.inner, isIE11 && styles.innerIE11()),
        "data-tid": ScrollContainerDataTids.inner,
        onScroll: _this.handleNativeScroll
      }, props.children)));
    };

    _this.renderScrollbar = function (axis) {
      var _this$getProps = _this.getProps(),
          offsetY = _this$getProps.offsetY,
          offsetX = _this$getProps.offsetX,
          invert = _this$getProps.invert,
          disableAnimations = _this$getProps.disableAnimations;

      var isAxisX = axis === 'x';
      var refScrollBar = isAxisX ? _this.refScrollBarX : _this.refScrollBarY;
      var offset = isAxisX ? offsetX : offsetY;
      var isVisible = isAxisX ? _this.state.isScrollBarXVisible : _this.state.isScrollBarYVisible;
      return /*#__PURE__*/React.createElement(ScrollBar, {
        axis: axis,
        ref: refScrollBar,
        invert: invert,
        onScroll: _this.handleScroll,
        offset: offset,
        disableAnimations: disableAnimations,
        isVisible: isVisible
      });
    };

    _this.handleScrollStateChange = function (scrollState, axis) {
      if (!_this.scrollY || !_this.scrollX) {
        return;
      }

      if (axis === 'x') {
        var scrollXState = convertScrollbarXScrollState(scrollState);
        _this.props.onScrollStateChangeX == null ? void 0 : _this.props.onScrollStateChangeX(scrollXState);
        return;
      }

      var scrollYState = convertScrollbarYScrollState(scrollState);
      _this.props.onScrollStateChange == null ? void 0 : _this.props.onScrollStateChange(scrollYState);
      _this.props.onScrollStateChangeY == null ? void 0 : _this.props.onScrollStateChangeY(scrollYState);
    };

    _this.handleScroll = function (axis, scrollState, prevScrollState) {
      if (scrollState !== prevScrollState) {
        _this.handleScrollStateChange(scrollState, axis);
      }

      var _this$getProps2 = _this.getProps(),
          hideScrollBar = _this$getProps2.hideScrollBar,
          showScrollBar = _this$getProps2.showScrollBar;

      (hideScrollBar || showScrollBar === 'scroll') && _this.showScrollBarOnMouseWheel(axis);
    };

    _this.refScrollBarY = function (scrollbar) {
      _this.scrollY = scrollbar;
    };

    _this.refScrollBarX = function (scrollbar) {
      _this.scrollX = scrollbar;
    };

    _this.refInner = function (element) {
      if (!_this.inner && element && _this.getProps().preventWindowScroll) {
        element.addEventListener('wheel', _this.handleInnerScrollWheel, {
          passive: false
        });
      }

      if (_this.inner && !element) {
        _this.inner.removeEventListener('wheel', _this.handleInnerScrollWheel);
      }

      _this.inner = element;
    };

    _this.handleNativeScroll = function (event) {
      var _this$scrollY, _this$scrollX;

      (_this$scrollY = _this.scrollY) == null ? void 0 : _this$scrollY.reflow();
      (_this$scrollX = _this.scrollX) == null ? void 0 : _this$scrollX.reflow();
      _this.props.onScroll == null ? void 0 : _this.props.onScroll(event);

      if (_this.getProps().preventWindowScroll) {
        event.preventDefault();
        return;
      }

      LayoutEvents.emit();
    };

    _this.showScrollBarOnMouseWheel = function (axis) {
      var isScrollBarVisible = axis === 'x' ? _this.state.isScrollBarXVisible : _this.state.isScrollBarYVisible;

      if (!isScrollBarVisible) {
        axis === 'x' ? _this.setState({
          isScrollBarXVisible: true
        }) : _this.setState({
          isScrollBarYVisible: true
        });
      }

      _this.hideScrollBar(axis);
    };

    _this.hideScrollBar = debounce(function (axis) {
      var _this$scrollX2, _this$scrollY2;

      if (_this.state.isHovered) {
        return;
      }

      var isScrollBarXHovered = (_this$scrollX2 = _this.scrollX) == null ? void 0 : _this$scrollX2.getHover();
      var isScrollBarYHovered = (_this$scrollY2 = _this.scrollY) == null ? void 0 : _this$scrollY2.getHover();

      if (axis === 'both') {
        !isScrollBarXHovered && !isScrollBarYHovered ? _this.setState({
          isScrollBarXVisible: false,
          isScrollBarYVisible: false
        }) : _this.hideScrollBar('both');
      } else if (axis === 'x') {
        !isScrollBarXHovered ? _this.setState({
          isScrollBarXVisible: false
        }) : _this.hideScrollBar('x');
      } else {
        !isScrollBarYHovered ? _this.setState({
          isScrollBarYVisible: false
        }) : _this.hideScrollBar('y');
      }
    }, _this.getProps().hideScrollBarDelay);

    _this.handleInnerScrollWheel = function (event) {
      if (!_this.inner || !isInstanceOf(event, globalObject.WheelEvent)) {
        return;
      }

      var axis = event.shiftKey ? 'x' : 'y';

      if (_this.hasScrollBar(axis)) {
        var _scrollSizeParameters = scrollSizeParametersNames[axis],
            pos = _scrollSizeParameters.pos,
            size = _scrollSizeParameters.size,
            offset = _scrollSizeParameters.offset;

        if (event.deltaY > 0 && _this.inner[size] <= _this.inner[pos] + _this.inner[offset]) {
          event.preventDefault();
          return false;
        }

        if (event.deltaY < 0 && _this.inner[pos] <= 0) {
          event.preventDefault();
          return false;
        }
      }
    };

    _this.handleMouseMove = function (event) {
      var _this$scrollY3, _this$scrollX3;

      var right = getDOMRect(event.currentTarget).right - event.pageX;
      var bottom = getDOMRect(event.currentTarget).bottom - event.pageY;
      (_this$scrollY3 = _this.scrollY) == null ? void 0 : _this$scrollY3.setHover(right <= 12);
      (_this$scrollX3 = _this.scrollX) == null ? void 0 : _this$scrollX3.setHover(right >= 12 && bottom <= 12);
      _this.getProps().showScrollBar === 'hover' && !_this.state.isHovered && _this.setState({
        isScrollBarXVisible: true,
        isScrollBarYVisible: true,
        isHovered: true
      });
    };

    _this.handleMouseLeave = function () {
      var _this$scrollY4, _this$scrollX4;

      (_this$scrollY4 = _this.scrollY) == null ? void 0 : _this$scrollY4.setHover(false);
      (_this$scrollX4 = _this.scrollX) == null ? void 0 : _this$scrollX4.setHover(false);

      if (_this.getProps().showScrollBar === 'hover') {
        _this.setState({
          isHovered: false
        });

        _this.hideScrollBar('both');
      }
    };

    _this.updateInnerElement = function () {
      var _this$scrollX5, _this$scrollY5;

      (_this$scrollX5 = _this.scrollX) == null ? void 0 : _this$scrollX5.setInnerElement(_this.inner);
      (_this$scrollY5 = _this.scrollY) == null ? void 0 : _this$scrollY5.setInnerElement(_this.inner);
    };

    return _this;
  }

  var _proto = ScrollContainer.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.updateInnerElement();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var preventWindowScroll = this.getProps().preventWindowScroll;

    if (this.inner) {
      if (prevProps.preventWindowScroll && !preventWindowScroll) {
        this.inner.removeEventListener('wheel', this.handleInnerScrollWheel);
      }

      if (!prevProps.preventWindowScroll && preventWindowScroll) {
        this.inner.addEventListener('wheel', this.handleInnerScrollWheel, {
          passive: false
        });
      }
    }

    if (prevProps.disabled !== this.props.disabled && !this.props.disabled) {
      this.updateInnerElement();
    }

    if (prevProps.showScrollBar !== this.props.showScrollBar) {
      if (this.props.showScrollBar === 'always') {
        this.setState({
          isScrollBarXVisible: true,
          isScrollBarYVisible: true
        });
      } else if (this.props.showScrollBar === 'never') {
        this.setState({
          isScrollBarXVisible: false,
          isScrollBarYVisible: false
        });
      }
    }
  };
  /**
  * @public
  * @param {Element} element
  */


  _proto.scrollTo = function scrollTo(element) {
    if (!element || !this.inner) {
      return;
    }

    this.inner.scrollLeft = element.offsetLeft;
    this.inner.scrollTop = getScrollYOffset(element, this.inner);
  }
  /**
  * @public
  */
  ;

  _proto.scrollToTop = function scrollToTop() {
    if (!this.inner) {
      return;
    }

    this.inner.scrollTop = 0;
  }
  /**
  * @public
  */
  ;

  _proto.scrollToBottom = function scrollToBottom() {
    if (!this.inner) {
      return;
    }

    this.inner.scrollTop = this.inner.scrollHeight - this.inner.offsetHeight;
  }
  /**
  * @public
  */
  ;

  _proto.scrollToLeft = function scrollToLeft() {
    if (!this.inner) {
      return;
    }

    this.inner.scrollLeft = 0;
  }
  /**
  * @public
  */
  ;

  _proto.scrollToRight = function scrollToRight() {
    if (!this.inner) {
      return;
    }

    this.inner.scrollLeft = this.inner.scrollWidth - this.inner.offsetWidth;
  };

  _proto.hasScrollBar = function hasScrollBar(axis) {
    if (!this.inner) {
      return false;
    }

    return axis === 'x' ? this.inner.offsetWidth < this.inner.scrollWidth : this.inner.offsetHeight < this.inner.scrollHeight;
  };

  return ScrollContainer;
}(React.Component), _class2.__KONTUR_REACT_UI__ = 'ScrollContainer', _class2.displayName = 'ScrollContainer', _class2.propTypes = {
  invert: PropTypes.bool,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scrollBehaviour: PropTypes.oneOf(['auto', 'smooth']),
  preventWindowScroll: PropTypes.bool,
  onScrollStateChange: PropTypes.func
}, _class2.defaultProps = {
  invert: false,
  scrollBehaviour: 'auto',
  preventWindowScroll: false,
  hideScrollBar: false,
  disableAnimations: isTestEnv,
  hideScrollBarDelay: 500,
  showScrollBar: 'always'
}, _temp)) || _class;